import { type SupportMessage } from '@/contracts';
import { useLocale, useTranslations } from '@/i18n/use-locale';
import { relativeTime } from '@/lib/format-time';
import { cn } from '@/lib/utils';

/**
 * One message. The requester sits at the inline start and the team at the inline
 * end — `self-start` / `self-end` on a column flex, which follow the writing
 * direction, so the two sides swap with the locale instead of being pinned to a
 * physical edge. The body carries `dir="auto"`: an Arabic admin can be answering an
 * English message and vice versa, and each paragraph should run its own way.
 */
export function MessageBubble({ message }: { message: SupportMessage }) {
  const t = useTranslations('admin.support');
  const { locale } = useLocale();
  const fromTeam = message.authorKind !== 'user';

  return (
    <li
      data-author={message.authorKind}
      className={cn(
        'max-w-[85%] rounded-2xl border px-4 py-3 shadow-sm md:max-w-[70%]',
        fromTeam ? 'self-end border-pitch-100 bg-pitch-50' : 'self-start border-conc-100 bg-white',
      )}
    >
      <p className="flex items-baseline gap-2 text-[11.5px] text-conc-500">
        <span className="font-bold text-navy">
          {fromTeam ? message.authorName || t('thread.team') : (message.authorName ?? '')}
        </span>
        <time dateTime={message.createdAt} className="num">
          {relativeTime(new Date(message.createdAt), locale)}
        </time>
      </p>
      <p dir="auto" className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-navy">
        {message.body}
      </p>
    </li>
  );
}
